import { useMemo } from "react";
import { get_process_details } from "../../api";
import { useKeyedRequest } from "../../utils/useKeyedRequest";
import { selectOverviewInsight } from "./overviewInsight";
import type { ProcessMetric } from "./processOverview";

export function useOverviewInsight(
  processes: ProcessMetric[],
  userIds: string[],
  from: string,
  to: string,
  enabled: boolean,
) {
  const top = processes.filter(process => process.seconds > 0).slice(0, 3);
  // Details cover all dates; changing dates reapplies the local scope without another download.
  // Updated summary figures invalidate evidence after new analysis, even for the same slugs.
  const key =
    enabled && userIds.length && top.length
      ? JSON.stringify([
          [...userIds].sort(),
          top.map(process => [process.slug, process.count, process.seconds]),
        ])
      : null;
  const request = useKeyedRequest(key, async signal => {
    const results = await Promise.allSettled(
      top.map(process => get_process_details(process.slug, signal)),
    );
    // A failed detail leaves the summary fallback usable and does not discard other evidence.
    return results.flatMap(result => (result.status === "fulfilled" ? [result.value] : []));
  });
  return useMemo(() => {
    const insight = selectOverviewInsight(processes, request.data ?? [], userIds, from, to);
    // Until the details arrive the top process stands in, and a finding may then replace it.
    return insight && { ...insight, pending: request.loading };
  }, [processes, request.data, request.loading, JSON.stringify(userIds), from, to]);
}
